'use client';

import { useTranslations } from 'next-intl';
import { Fragment } from 'react';
import { BaseLineUp2 } from '@/app/components/icons/BaseLineUp2';
import { Logo } from '@/app/components/icons/Logo';
import { LogoDark } from '@/app/components/icons/LogoDark';
import { ClientLink } from '@/app/components/shared/ClientLink';
import { COPY_RIGHT, isEnterpriseDistribution, QRCODE_URL, SITE } from '@/app/constants';
import { cn } from '@/app/utils';
import { ReportEvent } from './ReportEvent';
import type { RegistrationConfig } from './types';
import { getFooterConfig, getRegistrationConfig, getSocialLinks, getTermsAndPrivacy } from './utils';

interface FooterProps {
  className?: string;
  theme?: 'light' | 'dark';
}

export const Footer = ({ className, theme = 'dark' }: FooterProps) => {
  const trans = useTranslations('home3.footer');

  if (isEnterpriseDistribution) return null;

  const socialLinks = getSocialLinks();

  const footerConfig = getFooterConfig();

  const termsAndPrivacy = getTermsAndPrivacy();

  const registrationConfig = getRegistrationConfig();

  const footerRegRender = (item: RegistrationConfig, index: number, length: number) => (
    <Fragment key={item.id}>
      <div className="flex items-center sm:justify-center">
        {item.href ? (
          <ClientLink
            href={item.href}
            target={item.target}
            className={cn(
              'flex items-center hover:cursor-pointer',
              theme === 'light' ? 'text-[#ABABAB] hover:text-[#000000]' : 'text-white48 hover:text-white',
            )}
            rel={item.rel}
          >
            {item.icon && <img src={item.icon} alt={item.alt} className="mr-1 h-5 w-5 md:h-4 md:w-4" />}
            {item.text}
          </ClientLink>
        ) : (
          <span className={cn(theme === 'light' ? 'text-[#ABABAB]' : 'text-white48')}>{item.text}</span>
        )}
      </div>
      {index < length - 1 && (
        <span className={cn(theme === 'light' ? 'text-[#ABABAB]' : 'text-white48', 'mx-4 sm:hidden')}>
          <span className="text-[#FFFFFF29]">|</span>
        </span>
      )}
    </Fragment>
  );

  return (
    <footer
      data-google-interstitial="false"
      className={cn(
        'relative pb-6 sm:pt-20 md:pt-32',
        theme === 'light' ? 'bg-white text-[#333333]' : 'text-system-white64',
        className,
      )}
    >
      <div className="mx-20 sm:mx-4 lg:mx-[30px]">
        <div className="mb-8 flex justify-between py-3 sm:flex sm:flex-col sm:items-center sm:gap-8">
          <div className="sm:flex sm:flex-col sm:items-center">
            {theme === 'dark' ? (
              <Logo className={cn('text-3xl', 'text-white')} />
            ) : (
              <LogoDark className={cn('text-3xl')} />
            )}
            <div>
              <p className={cn('mt-6 mb-8 sm:text-center', theme === 'light' ? 'text-[#333333]' : 'text-[#ececec]')}>
                {trans('title')}
              </p>
              <div className="flex space-x-3 sm:justify-center">
                {socialLinks.map(({ icon: Icon, href, label }) => (
                  <ReportEvent key={`social_${label}`} eventName="element_click" params={{ element_name: label }}>
                    <ClientLink
                      href={href}
                      target="_blank"
                      title={label}
                      className={cn(
                        'flex h-11 w-11 items-center justify-center rounded-full border-[1px]',
                        theme === 'light' ? 'border-[#333333] text-[#333333]' : 'border-[#FFFFFF] text-white',
                      )}
                      rel="noopener noreferrer"
                    >
                      <Icon className="size-[24px]" />
                    </ClientLink>
                  </ReportEvent>
                ))}
              </div>
            </div>
          </div>
          {footerConfig.map(({ title, children, id }) => (
            <div key={id} className="flex flex-col items-start gap-2 px-4 text-sm leading-7 sm:items-center">
              <div className={cn('mb-3 text-base uppercase', theme === 'light' ? 'text-[#333333]' : 'text-white')}>
                {title}
              </div>
              {children.map(({ href, text, rel, target, reportElement }) => {
                const clientLinkElement = (
                  <ClientLink
                    key={href}
                    href={href}
                    target={target}
                    className={cn(
                      'hover:cursor-pointer',
                      theme === 'light' ? 'text-[#333333] hover:text-[#000000]' : 'text-system-text03 hover:text-white',
                      text === 'View All' && 'flex items-center gap-2',
                    )}
                    rel={rel}
                  >
                    {text}
                    {text === 'View All' && <BaseLineUp2 className="size-[16px] rotate-90" />}
                  </ClientLink>
                );

                return reportElement ? (
                  <ReportEvent
                    key={reportElement.key}
                    eventName={reportElement.eventName}
                    params={reportElement.params}
                  >
                    {clientLinkElement}
                  </ReportEvent>
                ) : (
                  clientLinkElement
                );
              })}
            </div>
          ))}
          {SITE === 'china' && (
            <div className="flex flex-col gap-2 px-4 text-center text-sm leading-7">
              <div className="mb-3 font-medium text-base">Vidu 官方交流群</div>
              <img src={QRCODE_URL} alt="wechat code" loading="lazy" className="h-[120px] w-[120px]" />
            </div>
          )}
        </div>
        <div
          className={cn(
            'flex justify-between border-t-[1px] pt-5 text-sm sm:flex-col sm:gap-2 sm:text-center',
            theme === 'light' ? 'border-t-[#ABABAB] text-[#ABABAB]' : 'border-t-[#383838] text-white48',
          )}
        >
          {SITE === 'china' ? (
            <div className="flex w-full flex-col sm:items-center">
              <div className="mb-2 flex flex-wrap justify-center sm:flex-col sm:items-center sm:space-x-0 sm:space-y-1">
                {registrationConfig.map((item, index) => footerRegRender(item, index, registrationConfig.length))}
              </div>
              <div className="flex flex-wrap items-center justify-center sm:flex-col sm:items-center">
                <div className="flex items-center justify-center">
                  {termsAndPrivacy.map((item, index) => footerRegRender(item, index, termsAndPrivacy.length))}
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="flex gap-8 sm:flex-col sm:items-center sm:gap-2">
                <span className={cn(theme === 'light' ? 'text-[#ABABAB]' : 'text-white48')}>{COPY_RIGHT}</span>
              </div>
              <div className="space-x-8">
                {termsAndPrivacy.map(
                  ({ href, text, target, rel, id }) =>
                    href && (
                      <ClientLink
                        key={id}
                        href={href}
                        target={target}
                        className={cn(
                          'hover:cursor-pointer',
                          theme === 'light' ? 'text-[#ABABAB] hover:text-[#000000]' : 'text-white48 hover:text-white',
                        )}
                        rel={rel}
                      >
                        {text}
                      </ClientLink>
                    ),
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </footer>
  );
};
